import { useState, useCallback, useRef, useEffect } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { cn } from "src/lib/cn";
import { sendAppEvent } from "src/Utils";
import StyledSelect from "../../StyledSelect";
import { useCharacterTemplates } from "../../Characters/useCharacterTemplates";

type InputProps = {
  onGo: (
    customInstructions: string,
    extra?: {
      role?: string;
      roleLabel?: string;
      fields?: Array<{ id?: string; label: string; value?: unknown }>;
      roleExplicitlySelected?: boolean;
    },
  ) => void;
  onCancel: () => void;
};

export const GenerateCharacterInput: React.FC<InputProps> = ({ onGo, onCancel }) => {
  const { roleOptions, getTemplateForRole } = useCharacterTemplates();
  const [instructions, setInstructions] = useState("");
  const [selectedRole, setSelectedRole] = useState<string | undefined>(roleOptions[0]?.value);
  const [roleExplicitlySelected, setRoleExplicitlySelected] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!selectedRole && roleOptions.length > 0) {
      setSelectedRole(roleOptions[0].value);
    }
  }, [roleOptions, selectedRole]);

  useEffect(() => {
    setTimeout(() => textareaRef.current?.focus(), 300);
  }, []);

  const selectedOption = roleOptions.find((o: any) => o.value === selectedRole) || roleOptions[0] || null;

  const handleSubmit = useCallback(() => {
    const template = roleExplicitlySelected ? getTemplateForRole(selectedRole) : null;
    onGo(instructions.trim(), {
      ...(roleExplicitlySelected && selectedRole
        ? { role: selectedRole, roleLabel: selectedOption?.label }
        : {}),
      ...(template?.defaultFields?.length ? { fields: template.defaultFields } : {}),
      roleExplicitlySelected,
    });
  }, [instructions, selectedRole, selectedOption, roleExplicitlySelected, getTemplateForRole, onGo]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <>
      {roleOptions.length > 0 && (
        <div className="w-full max-w-[200px]">
          <StyledSelect
            value={selectedOption}
            options={roleOptions}
            onChange={(option: any) => {
              setSelectedRole(option?.value);
              setRoleExplicitlySelected(true);
              if (option?.value != null) {
                const tmpl = getTemplateForRole(option.value);
                void sendAppEvent("Chat CTA - Character Role Selected", {
                  role: option.value,
                  template_source: tmpl?.source ?? "unknown",
                  is_custom: tmpl?.source === "custom" || tmpl?.source === "customOverride",
                });
              }
            }}
            placeholder="Select type"
            aria-label="Select the character role template"
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPosition="fixed"
            menuPlacement="auto"
            maxMenuHeight={200}
            menuShouldScrollIntoView={false}
            closeMenuOnScroll={false}
            styleObjectOverrides={{
              menuPortal: (base: any) => ({
                ...base,
                pointerEvents: "auto",
                zIndex: 30000,
              }),
              menuList: (base: any) => ({
                ...base,
                overscrollBehavior: "contain",
                paddingTop: 0,
                paddingBottom: 0,
              }),
            }}
          />
        </div>
      )}
      <TextareaAutosize
        ref={textareaRef}
        value={instructions}
        onChange={(e) => setInstructions(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Tell Sudowrite how to write this (optional)..."
        className={cn(
          "w-full resize-none rounded-lg border border-solid border-app-grey-2 bg-app-document-background",
          "px-3 py-2 text-sm text-app-grey-0 placeholder:text-app-grey-1",
          "focus:outline-none focus:border-app-primary-cta",
          "font-[inherit]"
        )}
        minRows={2}
        maxRows={10}
      />
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={onCancel}
          className={cn(
            "bg-transparent border-0 cursor-pointer",
            "text-app-grey-1 hover:text-app-grey-0 transition-colors",
            "text-sm font-medium px-2 py-1"
          )}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className={cn(
            "flex items-center gap-1.5 rounded-lg px-3 py-1.5",
            "bg-app-primary-cta text-white border-0 cursor-pointer",
            "hover:brightness-110 active:scale-[0.98] transition-all",
            "text-sm font-semibold"
          )}
        >
          Go!
          <span className="flex items-center gap-0.5 opacity-70 text-xs">
            <kbd className="font-sans">⌘</kbd>
            <kbd className="font-sans">↵</kbd>
          </span>
        </button>
      </div>
    </>
  );
};
